import { Group, Plus } from "iconoir-react/regular";
import { useTranslation } from "react-i18next";
import { PageHeader } from "shared/components/layouts/PageHeader/PageHeader";
import { Flex, Grid } from "styled-system/jsx";
import { useOperatorEvents } from "./useOperatorEvents";
import { EventCreateEditModal } from "shared/components/organisms/EventCreationAndEditing/EventCreateEditModal";
import { EnumOwnerType, EventManyAppQuery } from "gql/generated";
import { Icon, Switch } from "@ttc3k/trekker";
import { Text } from "@ttc3k/trekker";
import {
  CalendarMinus,
  CheckCircle,
  Eye,
  LongArrowUpLeft,
  Page,
  XmarkCircle,
} from "iconoir-react";
import {
  EventOperatorCard,
  PageContent,
  ViewSubSection,
} from "shared/components";
import { AnimatePresence, motion } from "framer-motion";
import { ModalMode } from "apps/events/types/ModalMode";

type RenderEventCardsProps = {
  title: string;
  Icon: React.ForwardRefExoticComponent<
    Omit<React.SVGProps<SVGSVGElement>, "ref"> &
      React.RefAttributes<SVGSVGElement>
  >;
  events: EventManyAppQuery["eventMany"];
  editMode: ModalMode;
  handleEditEvent: (eventId: string, editMode: ModalMode) => void;
  borderStyle?: "solid" | "dashed";
};

const RenderEventCards = ({
  title,
  Icon: IconElement,
  events,
  editMode,
  handleEditEvent,
  borderStyle,
}: RenderEventCardsProps) => (
  <ViewSubSection title={title} Icon={IconElement}>
    <Grid
      gridTemplateColumns="repeat(auto-fill, minmax(382px, 1fr))"
      alignItems="stretch"
      gridColumnGap="250"
      gridRowGap="250"
      gap="0"
    >
      {events.map((event) => (
        <EventOperatorCard
          key={event._id}
          event={event}
          onEditClick={() => handleEditEvent(event._id, editMode)}
          borderStyle={borderStyle}
        />
      ))}
    </Grid>
  </ViewSubSection>
);

export const OperatorEvents = () => {
  const { t } = useTranslation("directory", { keyPrefix: "OPERATOR.EVENTS" });

  const {
    email,
    isCreateEditModalOpen,
    mode,
    defaultOperatorEntityIds,
    defaultOrganizationId,
    limitHostToOperatorEntityId,
    selectedEventId,
    userType,
    loadingEvents,
    errorEvents,
    cancelledEvents,
    declinedEvents,
    draftEvents,
    inReviewEvents,
    approvedEvents,
    pastEvents,
    showPastEvents,
    handleCloseCreateEditModal,
    handleCreateNewEvent,
    handleEditEvent,
    handleUpdateOrAddEvent,
    handleRemoveEvent,
    handlePastEventsToggle,
  } = useOperatorEvents();

  return (
    <>
      <Flex flexDir={"column"} gap={"500"}>
        <PageHeader
          title={t("TITLE")}
          Icon={Group}
          description={t("DESCRIPTION")}
          ctaButtonCopy={t("CTA_BUTTON.CREATE")}
          ctaButtonProps={{
            Icon: Plus,
            onClick: handleCreateNewEvent,
          }}
        />

        <PageContent loading={loadingEvents} showSimpleError={!!errorEvents}>
          {/* Toggle past events ON/OFF */}
          {pastEvents.length > 0 && (
            <>
              <Flex
                flexDir="row"
                justifyContent="space-between"
                alignItems="center"
                p="250"
                border="1px solid"
                borderColor="border.light"
                bgColor="bg.wa.lightest"
                borderRadius="100"
              >
                <Flex flexDir="row" alignItems="center" gap="100">
                  <Icon Element={LongArrowUpLeft} w="24px" h="24px" />
                  <Text visual="bodySemiBold">
                    {showPastEvents
                      ? t("PAST_EVENTS.HIDE", { count: pastEvents.length })
                      : t("PAST_EVENTS.SHOW", { count: pastEvents.length })}
                  </Text>
                </Flex>
                <Switch
                  checked={showPastEvents}
                  onChange={handlePastEventsToggle}
                  aria-label={
                    showPastEvents
                      ? t("PAST_EVENTS.HIDE", { count: pastEvents.length })
                      : t("PAST_EVENTS.SHOW", { count: pastEvents.length })
                  }
                />
              </Flex>

              <AnimatePresence mode="wait">
                {showPastEvents && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    {RenderEventCards({
                      title: t("PAST_EVENTS.TITLE"),
                      Icon: LongArrowUpLeft,
                      events: pastEvents,
                      editMode: "edit",
                      handleEditEvent,
                      borderStyle: "dashed",
                    })}
                  </motion.div>
                )}
              </AnimatePresence>
            </>
          )}

          {/* DRAFT EVENTS */}
          {draftEvents.length > 0 &&
            RenderEventCards({
              title: t("DRAFT_EVENTS.TITLE"),
              Icon: Page,
              events: draftEvents,
              editMode: "edit",
              handleEditEvent,
              borderStyle: "dashed",
            })}

          {/* IN REVIEW EVENTS */}
          {inReviewEvents.length > 0 &&
            RenderEventCards({
              title: t("IN_REVIEW_EVENTS.TITLE"),
              Icon: Eye,
              events: inReviewEvents,
              // Organization users can start in the approve mode
              // Operator users don't have the approve mode
              editMode:
                userType === EnumOwnerType.Organization ? "approve" : "edit",
              handleEditEvent,
            })}

          {/* APPROVED EVENTS */}
          {approvedEvents.length > 0 &&
            RenderEventCards({
              title: t("APPROVED_EVENTS.TITLE"),
              Icon: CheckCircle,
              events: approvedEvents,
              editMode: "edit",
              handleEditEvent,
            })}

          {/* CANCELLED EVENTS */}
          {cancelledEvents.length > 0 &&
            RenderEventCards({
              title: t("CANCELLED_EVENTS.TITLE"),
              Icon: CalendarMinus,
              events: cancelledEvents,
              editMode: "edit",
              handleEditEvent,
              borderStyle: "dashed",
            })}

          {/* DECLINED EVENTS */}
          {declinedEvents.length > 0 &&
            RenderEventCards({
              title: t("DECLINED_EVENTS.TITLE"),
              Icon: XmarkCircle,
              events: declinedEvents,
              editMode: "edit",
              handleEditEvent,
              borderStyle: "dashed",
            })}
        </PageContent>
      </Flex>
      <EventCreateEditModal
        isOpen={isCreateEditModalOpen}
        userEmailAddress={email}
        userType={userType as EnumOwnerType | "PUBLIC"}
        initialMode={mode}
        defaultOrganizationId={defaultOrganizationId}
        defaultOperatorEntityIds={defaultOperatorEntityIds}
        limitHostToOperatorEntityId={limitHostToOperatorEntityId}
        eventId={selectedEventId}
        onClose={handleCloseCreateEditModal}
        onEventUpdated={handleUpdateOrAddEvent}
        onEventDeleted={handleRemoveEvent}
      />
    </>
  );
};
